import { format } from 'date-fns-tz';
import * as React from 'react';
import { type ReactNode, useState } from 'react';
import { type DateRange, type DayPickerRangeProps } from 'react-day-picker';

import { Button } from '#/app/components/ui/button.tsx';
import { Calendar } from '#/app/components/ui/calendar.tsx';
import { Icon } from '#/app/components/ui/icon.tsx';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '#/app/components/ui/popover.tsx';
import { cn } from '#/app/utils/misc.tsx';

export type DatePickerProps = Omit<DayPickerRangeProps, 'mode' | 'onSelect'> & {
  onSelect: (range: DateRange | undefined) => void;
};
export function DatePickerRange({
  fromDate,
  toDate,
  className,
  ...props
}: DatePickerProps) {
  const [range, setRange] = useState<DateRange | undefined>({
    from: fromDate,
    to: toDate,
  });
  const [isOpen, setIsOpen] = useState(false);

  const setDate = (date: Date) => {
    if (range?.from && range?.to) {
      setRange({ from: date, to: undefined });
      return;
    }

    if (range?.from && !range?.to && date < range.from) {
      setRange({ from: date, to: undefined });
      return;
    }

    if (range?.from && !range?.to && date === range.from) {
      const newRange = { from: range.from, to: date };
      setRange(newRange);
      props.onSelect(newRange);
      setIsOpen(false);
      return;
    }

    if (range?.from && !range?.to) {
      const newRange = { from: range.from, to: date };
      setRange(newRange);
      props.onSelect(newRange);
      setIsOpen(false);
    }
  };

  let selectedDate: ReactNode = <span>Elige una fecha</span>;
  if (fromDate) {
    selectedDate = `${format(fromDate, 'dd LLL yyyy')}`;

    if (toDate) {
      const toDateString = format(toDate, 'dd LLL yyyy');

      if (toDateString !== selectedDate) {
        selectedDate += ` - ${format(toDate, 'dd LLL yyyy')}`;
      }
    }
  }

  const onOpenChange = (isOpen: boolean) => {
    if (!isOpen && range?.from && !range?.to) {
      const newRange = { from: range.from, to: range.from };
      props.onSelect(newRange);
    }
    setIsOpen(isOpen);
  };

  return (
    <Popover open={isOpen} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          className={cn(
            'flex max-h-10 w-full items-center justify-between rounded-md border border-input bg-secondary px-5 py-2 text-body ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 [&>span]:line-clamp-1',
            className,
          )}
        >
          <div className="flex items-baseline whitespace-nowrap border-r py-2 pl-2 pr-4">
            <Icon name="calendar" className="mr-2 h-4 w-4" />
            {selectedDate}
          </div>
          <Icon
            name={isOpen ? 'chevron-up' : 'chevron-down'}
            className="ml-2 h-4 w-4"
          />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0">
        <Calendar
          {...props}
          onSelect={(range, selectedDate) => {
            setDate(selectedDate);
          }}
          defaultMonth={fromDate}
          selected={range}
          mode="range"
          numberOfMonths={2}
        />
      </PopoverContent>
    </Popover>
  );
}
